import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {CustomOption, OptionGroup, SocialOption} from '../generic-toolbar/option-model';
import { StatelessBehavior } from '../generic-toolbar/state-behavior-model';
import { Icon } from '../generic-toolbar/icons';
import { CONSTANTS } from '@app/@shared/consts/platform-constants';
import {ContentService} from "@shared/services/content/content.service";
import {ShareItemModalComponent} from "@shared/components/share-item-modal/share-item-modal.component";
import {DialogService} from "@shared/services/dialog/dialog.service";
import {FeatureFlagService, ffKeys} from "@shared/services/featureflag.service";
import {DomSanitizer, SafeUrl} from "@angular/platform-browser";
import {environment} from "@env/environment";
import {Router} from "@angular/router";
import { SafariDownloadModalComponent } from '../safari-download-modal/safari-download-modal.component';

@Component({
    selector: 'app-html-viewer',
    templateUrl: './html-viewer.component.html',
    styleUrl: './html-viewer.component.scss',
    standalone: false
})
export class HtmlViewerComponent implements OnInit {
  @ViewChild('iframe', { static: true}) iframe: ElementRef<HTMLIFrameElement>;

  @Input() set url(value: string) {
    this.safeUrl = this.sanitize.bypassSecurityTrustResourceUrl(value);
    this._url = value;
  }
  @Input() downloadPDFEnabled = false;
  @Output() closeAssetViewer: EventEmitter<any> = new EventEmitter<any>();

  public toolbarOptions: OptionGroup[];
  public innerText: string;
  public downLoadInProgress: boolean;
  public safeUrl: SafeUrl;

  private _url: string;
  private zoomLevel = 1;
  private readonly ZOOM_STEP = 0.1;
    private enableDownloadAndShareOptionsForHygraphHTMLViewer: boolean;

  constructor(
    private readonly contentService: ContentService,
    private readonly dialogService: DialogService,
    private readonly sanitize: DomSanitizer,
    private readonly router: Router,
    private readonly featureFragService: FeatureFlagService,
  ){}

  ngOnInit() {
    this.featureFragService.getFlag(ffKeys.enableDownloadAndShareOptionsForHygraphHTMLViewer).subscribe((flag) => {
      this.enableDownloadAndShareOptionsForHygraphHTMLViewer = flag;
    });
    this.toolbarOptions = this.createToolbarOptions();
  }

  ngAfterViewInit() {
    this.adjustIframeScale();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.adjustIframeScale();
  }

  public shareAsset() {
    this.dialogService.open(ShareItemModalComponent, { windowClass: 'shareModalClass' }, {
      isDocumentShareable: false,
      itemDeepLinkUrl: environment.basePath + this.router.url,
    });
  }

  public downloadDocument(): void {
    this.downLoadInProgress = true;

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari) {
      this.downLoadInProgress = false;
      const modal = this.dialogService.open(SafariDownloadModalComponent);
      modal.componentInstance.url = this._url;
    } else {
      this.contentService.getHTMLContent(this._url).subscribe(
        (response: string) => {
          this.innerText = response;
          const contentDownload = this.iframe.nativeElement.cloneNode(true) as HTMLIFrameElement;
          document.body.appendChild(contentDownload);
          contentDownload.style.position = 'absolute';
          contentDownload.style.width = '0';
          contentDownload.style.height = '0';
          contentDownload.style.border = 'none';
          contentDownload.contentDocument?.open();
          contentDownload.contentDocument?.write(this.innerText);
          contentDownload.contentDocument?.close();
          contentDownload.onload = () => {
            contentDownload.contentWindow?.print();
            document.body.removeChild(contentDownload);
            this.downLoadInProgress = false;
          };
        },
        (_error) => {
          this.downLoadInProgress = false;
        }
      );
    }
  }

  public closeViewer(): void {
    this.closeAssetViewer.emit();
  }

  public zoomIn(): void {
    this.zoomLevel += this.ZOOM_STEP;
    this.adjustIframeScale();
  }

  public zoomOut(): void {
    this.zoomLevel -= this.ZOOM_STEP;
    this.adjustIframeScale();
  }

  public fitPage(): void {
    this.zoomLevel = 1;
    this.adjustIframeScale();
  }

  public toggleFullScreen(): void {
    const iframeContainer = this.iframe.nativeElement;
    iframeContainer.requestFullscreen();
  }

  private createToolbarOptions(): OptionGroup[] {
    const optionGroups: OptionGroup[] = [];
    const closeOption: any[] = [];
    const userOption: any[] = [];
    const zoomOptions: any[] = [];

    closeOption.push(
      new CustomOption(
        'close-viewer',
        { click: () => this.closeViewer() },
        new StatelessBehavior(Icon.REPORT_VIEWER_CLOSE, 'Close'),
        'Close'
      )
    );

    if (this.enableDownloadAndShareOptionsForHygraphHTMLViewer) {
      if (this.downloadPDFEnabled) {
        userOption.push(
          new SocialOption(
            'download-pdf',
            { click: () => this.downloadDocument() },
            new StatelessBehavior(Icon.REPORT_VIEWER_DOWNLOAD, 'Download')
          )
        );
      }
    }

    if (this.enableDownloadAndShareOptionsForHygraphHTMLViewer) {
      userOption.push(
        new SocialOption(
          'share-asset',
          { click: () => this.shareAsset() },
          new StatelessBehavior(Icon.IMAGE_SHARE, 'Share')
        )
      );
    }

    zoomOptions.push(
      new CustomOption(
        'zoom-out',
        { click: () => this.zoomOut() },
        new StatelessBehavior(Icon.REPORT_VIEWER_ZOOM_OUT, 'Zoom Out')
      )
    );

    zoomOptions.push(
      new CustomOption(
        'zoom-in',
        { click: () => this.zoomIn() },
        new StatelessBehavior(Icon.REPORT_VIEWER_ZOOM_IN, 'Zoom In')
      )
    );

    zoomOptions.push(
      new CustomOption(
        'fit-to-page',
        { click: () => this.fitPage() },
        new StatelessBehavior(Icon.REPORT_VIEWER_FIT_PAGE, 'Fit to Page')
      )
    );

    zoomOptions.push(
      new CustomOption(
        'toggle-fullscreen',
        { click: () => this.toggleFullScreen() },
        new StatelessBehavior(Icon.REPORT_VIEWER_ENTER_FULLSCREEN, 'Full Screen')
      )
    );

    optionGroups.push(new OptionGroup(zoomOptions));
    optionGroups.push(new OptionGroup(userOption));
    optionGroups.push(new OptionGroup(closeOption));
    return optionGroups;
  }

  private adjustIframeScale() {
    const iframe = this.iframe.nativeElement;
    const viewportWidth = window.innerWidth;
    const viewportheight = window.innerHeight
    /**
     * Using fixed values for the report size (2048x1536),
     * no way to get the actual size of the report
     **/
    const scaleX = viewportWidth / CONSTANTS.HTML_IFRAME_WIDTH;
    const scaleY = viewportheight / CONSTANTS.HTML_IFRAME_HEIGHT;
    const scale = Math.min(scaleX, scaleY) * this.zoomLevel;
    iframe.style.transform = `scale(${scale})`;
    iframe.style.width = `${100 / scale}%`;
    // 48px is the height of the toolbar
    iframe.style.height = `calc((100% - 48px)/${scale})`;
  }
}
